import { FC, useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router";

import { useApi } from "../../api";
import { AuthForm } from "../wrappers";
import { useAuth, useEmailVerify } from "../hooks";
import { mapUserBodyToOnboardingAction } from "./onboardingMapping";
import { FormStep, onboardingSteps, handleResetStep } from "../general";
import {
  useAppDispatch,
  setOnboarding,
  fetchPages,
  fetchCompanies,
  useAppSelector,
  getCompany,
} from "../../store";

export const OnboardingForm: FC<React.PropsWithChildren> = () => {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const { companies } = useAppSelector((state) => state.companies);

  const { user } = useAuth();
  const navigate = useNavigate();
  const { getApi } = useApi();
  const dispatch = useAppDispatch();

  useEmailVerify();

  useEffect(() => {
    if (!companies || companies.length === 0 || !companies[0].id) return;

    const companId = companies[0].id;

    const handleGetCompany = async () => {
      const api = await getApi();
      await dispatch(getCompany({ api, value: companId }));

      navigate(`/company/${companId}`);
    };

    handleGetCompany();
  }, [companies, dispatch, getApi, navigate]);

  const handleFetchPages = useCallback(async () => {
    if (!user) return;

    const api = await getApi();
    await dispatch(fetchPages(api));
  }, [user, dispatch, getApi]);

  const handleFetchCompanies = useCallback(async () => {
    if (!user) return;

    const api = await getApi();
    return await dispatch(fetchCompanies(api));
  }, [user, dispatch, getApi]);

  const handleOnboarding = async (formSteps: FormStep[]) => {
    setErrorMessage("");
    setLoading(true);
    try {
      const api = await getApi();
      const putData = mapUserBodyToOnboardingAction(formSteps);
      const updateOnboardingResponse = await api.updateOnboarding(putData);

      if (updateOnboardingResponse.value.message !== "success") {
        setErrorMessage(updateOnboardingResponse.value.message);

        return;
      }
      await user?.getIdToken(true);
      onboardingSteps.map(handleResetStep);
      dispatch(setOnboarding(true));
      await handleFetchPages();
      await handleFetchCompanies();
    } catch (error) {
      setErrorMessage("There was an error. Please try again later");
      setLoading(false);
    }
  };

  return (
    <>
      {onboardingSteps.length > 0 && (
        <AuthForm
          submitTitle="Submit"
          registerSubmitCallback={handleOnboarding}
          errorMessage={errorMessage}
          setErrorMessage={setErrorMessage}
          steps={onboardingSteps}
          isLoading={loading}
        />
      )}
    </>
  );
};
